import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import "./styles/theme.css";

import ScrollToTop from "./components/site/ScrollToTop";
import Analytics from "./components/site/Analytics";
import PageShell from "./components/site/PageShell";

import Home from "./pages/Home";
import GuidesHub from "./pages/guides/GuidesHub";
import FaqPage from "./pages/FaqPage";
import Glossary from "./pages/Glossary";
import NotFound from "./pages/NotFound";

// Guides
import WhatIsAnEpsFile from "./pages/guides/WhatIsAnEpsFile";
import HowToOpenEpsFiles from "./pages/guides/HowToOpenEpsFiles";
import OpenEpsWithoutIllustrator from "./pages/guides/OpenEpsWithoutIllustrator";
import ConvertEpsToPng from "./pages/guides/ConvertEpsToPng";
import ConvertEpsToJpg from "./pages/guides/ConvertEpsToJpg";
import ConvertEpsToPdf from "./pages/guides/ConvertEpsToPdf";
import EpsVsSvgAiPdf from "./pages/guides/EpsVsSvgAiPdf";
import VectorVsRaster from "./pages/guides/VectorVsRaster";
import FixEpsFileProblems from "./pages/guides/FixEpsFileProblems";
import EpsFileFormatExplained from "./pages/guides/EpsFileFormatExplained";
import BestFreeEpsEditors from "./pages/guides/BestFreeEpsEditors";
import HowToCreateEpsFile from "./pages/guides/HowToCreateEpsFile";
import EpsFilesForLogoDesign from "./pages/guides/EpsFilesForLogoDesign";

// Legal / company (bare pages wrapped by PageShell)
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import Contact from "./pages/Contact";
import Disclaimer from "./pages/Disclaimer";
import AboutMagicApps from "./pages/AboutMagicApps";
import CookiePolicy from "./pages/CookiePolicy";

const App = (
  <BrowserRouter>
    <ScrollToTop />
    <Analytics />
    <Routes>
      <Route path="/" element={<Home />} />

      {/* Guides */}
      <Route path="/guides" element={<GuidesHub />} />
      <Route path="/guides/what-is-an-eps-file" element={<WhatIsAnEpsFile />} />
      <Route path="/guides/how-to-open-eps-files" element={<HowToOpenEpsFiles />} />
      <Route path="/guides/open-eps-without-illustrator" element={<OpenEpsWithoutIllustrator />} />
      <Route path="/guides/convert-eps-to-png" element={<ConvertEpsToPng />} />
      <Route path="/guides/convert-eps-to-jpg" element={<ConvertEpsToJpg />} />
      <Route path="/guides/convert-eps-to-pdf" element={<ConvertEpsToPdf />} />
      <Route path="/guides/eps-vs-svg-vs-ai-vs-pdf" element={<EpsVsSvgAiPdf />} />
      <Route path="/guides/vector-vs-raster" element={<VectorVsRaster />} />
      <Route path="/guides/fix-eps-file-problems" element={<FixEpsFileProblems />} />
      <Route path="/guides/eps-file-format-explained" element={<EpsFileFormatExplained />} />
      <Route path="/guides/best-free-eps-editors" element={<BestFreeEpsEditors />} />
      <Route path="/guides/how-to-create-an-eps-file" element={<HowToCreateEpsFile />} />
      <Route path="/guides/eps-files-for-logo-design" element={<EpsFilesForLogoDesign />} />

      <Route path="/faq" element={<FaqPage />} />
      <Route path="/glossary" element={<Glossary />} />

      {/* Legal / company */}
      <Route path="/privacy-policy" element={<PageShell title="Privacy Policy | EPS Viewer" description="Privacy Policy for EPS Viewer — how we handle file uploads, cookies, analytics and your data." path="/privacy-policy"><PrivacyPolicy /></PageShell>} />
      <Route path="/terms" element={<PageShell title="Terms & Conditions | EPS Viewer" description="The terms and conditions for using the EPS Viewer website and tools." path="/terms"><Terms /></PageShell>} />
      <Route path="/disclaimer" element={<PageShell title="Disclaimer | EPS Viewer" description="Disclaimer for the EPS Viewer website." path="/disclaimer"><Disclaimer /></PageShell>} />
      <Route path="/cookie-policy" element={<PageShell title="Cookie Policy | EPS Viewer" description="How EPS Viewer uses cookies and similar technologies." path="/cookie-policy"><CookiePolicy /></PageShell>} />
      <Route path="/contact" element={<PageShell title="Contact Us | EPS Viewer" description="Get in touch with the EPS Viewer team." path="/contact"><Contact /></PageShell>} />
      <Route path="/about-magicapps" element={<PageShell title="About | EPS Viewer by MagicApps" description="About EPS Viewer and MagicApps, the team behind it." path="/about-magicapps"><AboutMagicApps /></PageShell>} />

      {/* Redirects from old URLs */}
      <Route path="/eps" element={<Navigate to="/guides/what-is-an-eps-file" replace />} />
      <Route path="/blog" element={<Navigate to="/guides" replace />} />
      <Route path="/blog/convert-eps-to-png" element={<Navigate to="/guides/convert-eps-to-png" replace />} />
      <Route path="/blog/open-eps-without-illustrator" element={<Navigate to="/guides/open-eps-without-illustrator" replace />} />
      <Route path="/blog/eps-vs-svg-vs-ai-vs-pdf" element={<Navigate to="/guides/eps-vs-svg-vs-ai-vs-pdf" replace />} />
      <Route path="/blog/eps-file-problems" element={<Navigate to="/guides/fix-eps-file-problems" replace />} />

      {/* Fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  </BrowserRouter>
);

/**
 * Render, deliberately not hydrate.
 *
 * scripts/prerender.mjs snapshots each route with Puppeteer and writes
 * `document.documentElement.outerHTML`. Serialising a live DOM merges adjacent
 * text nodes: React renders `By {AUTHOR} · Updated {DATE}` as four text nodes,
 * but the saved HTML contains one. React's SSR emits `<!-- -->` separators to
 * preserve those boundaries; a DOM snapshot has no way to reproduce them.
 *
 * hydrateRoot therefore failed on every page with React error #418 and fell
 * back to regenerating the whole tree client-side anyway. The `{" "}` idiom
 * used for line-wrapping prose in the guides means this affects most routes,
 * so there is no small set of components to correct.
 *
 * createRoot does the same client render without the doomed hydration pass and
 * without the console error. The prerendered HTML still does its only job:
 * serving complete content to crawlers that do not execute JavaScript.
 */
createRoot(document.getElementById("root")).render(App);
